import { CircleDotIcon, PenIcon, StarFilledIcon, StarIcon, TrashIcon } from "@xola/icons";
import { assign } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { SellerPaymentMethod } from "types";
import { api } from "../../../api/api";
import { populateResource } from "../../../hooks/helper";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { NoTranslate } from "../../Common/NoTranslate";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";

interface PaymentMethodProps {
    paymentMethod: SellerPaymentMethod;
    onEdit: (paymentMethod: SellerPaymentMethod) => void;
}

export const PaymentMethodItem = ({ paymentMethod, onEdit }: PaymentMethodProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [isDelete, setDelete] = useState(false);

    const { mutateAsync: updatePaymentMethod } = useMutation(async (paymentMethod: SellerPaymentMethod) => {
        return api.put(`/users/${seller.id}/paymentMethods/${paymentMethod.id}`, paymentMethod);
    });

    const { mutateAsync: deletePaymentMethod } = useMutation(async () => {
        await api.delete(`users/${seller.id}/paymentMethods/${paymentMethod.id}`);
        const paymentMethods = seller.paymentMethods?.filter((method) => method.name !== paymentMethod.name);
        const updatedSeller = assign(seller, { paymentMethods });
        populateResource("sellers", updatedSeller);
    });

    const handleStarClick = async (e: any) => {
        e.stopPropagation();
        await updatePaymentMethod({ ...paymentMethod, favourite: !paymentMethod.favourite });
        invalidate("sellers", seller.id);
    };

    const handleDelete = (e: any) => {
        e.stopPropagation();
        toggleDeleteModal();
    };

    const toggleDeleteModal = () => {
        setDelete(!isDelete);
    };

    const handleDeleteConfirm = async () => {
        await deletePaymentMethod();
        invalidate("sellers", seller.id);
        toggleDeleteModal();
    };

    return (
        <>
            <div
                data-testid={`payment-method-item-${paymentMethod.name}`}
                className="flex w-full cursor-pointer rounded border border-gray-light p-3 hover:bg-gray-hover"
                onClick={() => onEdit(paymentMethod)}
            >
                <div className="flex flex-grow items-center space-x-2">
                    <div
                        data-testid="payment-method-item-favourite-button"
                        className="cursor-pointer"
                        onClick={handleStarClick}
                    >
                        {paymentMethod.favourite ? <StarFilledIcon size="medium" /> : <StarIcon size="medium" />}
                    </div>
                    <div data-testid="payment-method-item-edit-button">
                        <CircleDotIcon size="medium" />
                    </div>
                    <NoTranslate as="div" className="self-center">
                        {paymentMethod.name}
                    </NoTranslate>
                </div>

                <div className="flex space-x-2">
                    <div data-testid="payment-method-item-edit-button" className="self-center">
                        <SecondaryButton size="small" onClick={() => onEdit(paymentMethod)}>
                            <PenIcon />
                        </SecondaryButton>
                    </div>

                    <div data-testid="payment-method-item-remove-button" className="self-center">
                        <SecondaryButton size="small" onClick={handleDelete}>
                            <TrashIcon />
                        </SecondaryButton>
                    </div>
                </div>
            </div>

            <PromptModal
                title="Delete Payment Method?"
                isOpen={isDelete}
                onConfirm={handleDeleteConfirm}
                onClose={toggleDeleteModal}
            >
                Are you sure you want to delete this payment method?
            </PromptModal>
        </>
    );
};
